/**
 * Structure tab auxiliary tools — BIM parity additions:
 * - ReferencePlaneTool (Datum panel, Structure tab): pick line in 3D creating
 *   a vertical reference plane through it.
 * - RebarCouplerTool (Reinforcement panel): mechanical coupler between two
 *   rebar runs; two clicks define its axis.
 * - FabricSheetTool (Reinforcement panel): single standard welded-wire sheet
 *   placed flat over a host surface (one click = sheet center).
 * - ModelInPlaceTool (Model panel): project-specific structural solid built
 *   from a closed footprint sketch + extrusion height.
 */

import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from './BaseTool';
import {
    ReferencePlaneElement,
    RebarCouplerElement,
    Element,
} from '../core/types';

// ==================== Reference Plane (Datum panel) ====================

/**
 * Two clicks on the working plane define the trace of a vertical reference
 * plane (BIM: reference plane drawn in a plan view stands vertical).
 */
export class ReferencePlaneTool extends BaseTool {
    id = 'reference_plane';
    name = 'Reference Plane';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            return;
        }

        const start = this.startPoint;
        this.startPoint = null;
        this.context.appStore.setToolPreview(null);

        const dir = new THREE.Vector3().subVectors(snapped, start);
        if (dir.lengthSq() < 1e-6) return;
        dir.normalize();
        // Vertical plane: normal is horizontal, perpendicular to the trace.
        const normal = new THREE.Vector3(-dir.y, dir.x, 0);
        const mid = start.clone().add(snapped).multiplyScalar(0.5);
        const halfSpan = dir.clone().multiplyScalar(start.distanceTo(snapped) / 2 + 5);

        const plane: ReferencePlaneElement = {
            id: uuidv4(),
            category: 'Reference Planes',
            type: 'Reference Plane',
            parameters: {
                'Length': `${start.distanceTo(snapped).toFixed(2)} m`,
                'Is Reference Plane': 'Yes',
                'Defines Origin': 'No',
            },
            constraints: [],
            geometryData: {
                origin: [mid.x, mid.y, mid.z],
                normal: [normal.x, normal.y, normal.z],
                direction: [dir.x, dir.y, 0],
                extension: [halfSpan.length(), 10],
            },
            isReference: 'StrongReference',
            wallClosure: false,
        };

        this.context.bimStore.addElement(plane);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point && this.startPoint) {
            this.context.appStore.setToolPreview({
                type: 'line',
                start: this.startPoint,
                end: this.snapToGrid(point),
                color: '#33cc33',
            });
        }
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.startPoint = null;
            this.context?.appStore.setToolPreview(null);
        } else {
            super.onKeyDown(event);
        }
    }
}

// ==================== Rebar Coupler (Reinforcement panel) ====================

/**
 * Two clicks define the coupler axis (a short cylinder joining rebar runs).
 */
export class RebarCouplerTool extends BaseTool {
    id = 'rebar_coupler';
    name = 'Rebar Coupler';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            return;
        }

        const start = this.startPoint;
        this.startPoint = null;
        this.context.appStore.setToolPreview(null);
        if (start.distanceTo(snapped) < 1e-4) return;

        const diameter = (this.context.bimStore.toolProperties.couplerDiameter ?? 16) / 1000;
        const coupler: RebarCouplerElement = {
            id: uuidv4(),
            category: 'Rebar Couplers',
            type: 'Mechanical Coupler',
            parameters: {
                'Bar Diameter': `${(diameter * 1000).toFixed(0)} mm`,
                'Length': `${start.distanceTo(snapped).toFixed(2)} m`,
                'Coupler Type': 'Threaded',
            },
            constraints: [],
            geometryData: {
                start: [start.x, start.y, start.z],
                end: [snapped.x, snapped.y, snapped.z],
                barDiameter: diameter * 1000,
            },
        };

        this.context.bimStore.addElement(coupler);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point && this.startPoint) {
            this.context.appStore.setToolPreview({
                type: 'line',
                start: this.startPoint,
                end: this.snapToGrid(point),
                color: '#cd6946',
            });
        }
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.startPoint = null;
            this.context?.appStore.setToolPreview(null);
        } else {
            super.onKeyDown(event);
        }
    }
}

// ==================== Fabric Sheet (Reinforcement panel) ====================

/**
 * Places ONE standard factory welded-wire sheet (not a sketched area).
 * First click picks the host (floor/wall/foundation), second click sets the
 * sheet center on its top face.
 */
export interface FabricSheetElement extends Element {
    category: 'Structural Area Reinforcement';
    geometryData: {
        hostId: string;
        center: [number, number, number];
        /** Sheet size in meters along major/minor directions. */
        sheetLength: number;
        sheetWidth: number;
        rotation: number;
        sheetType: string;
    };
}

const FABRIC_SHEET_TYPES = [
    { name: 'A142 (6x6-W2.9/2.9)', length: 2.4, width: 4.8, wire: 2.9, spacing: 150 },
    { name: 'A193 (6x6-W4.0/4.0)', length: 2.4, width: 4.8, wire: 4.0, spacing: 150 },
    { name: 'B142 (5x5-W2.9/2.9)', length: 2.4, width: 4.8, wire: 2.9, spacing: 200 },
    { name: 'C193 (5x5-W4.0/4.0)', length: 2.4, width: 4.8, wire: 4.0, spacing: 200 },
] as const;

export class FabricSheetTool extends BaseTool {
    id = 'fabric_sheet';
    name = 'Fabric Sheet';
    cursor = 'crosshair';

    private hostId: string | null = null;
    private sheetIndex = 0;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        if (!this.hostId) {
            const hit = this.raycastSceneAtEvent(event).find(i => {
                const id = this.findId(i.object);
                const el = id ? this.context!.bimStore.project.elements.get(id) : null;
                return el && (el.category === 'Floors' || el.category === 'Walls' || el.category === 'Structural Foundations');
            });
            if (hit) {
                this.hostId = this.findId(hit.object)!;
            }
            return;
        }

        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);
        const sheet = FABRIC_SHEET_TYPES[this.sheetIndex % FABRIC_SHEET_TYPES.length];
        const overlap = (this.context.bimStore.toolProperties.fabricSheetOverlap ?? 200) / 1000;

        const element: FabricSheetElement = {
            id: uuidv4(),
            category: 'Structural Area Reinforcement',
            type: `Fabric Sheet: ${sheet.name}`,
            parameters: {
                'Fabric Designation': sheet.name,
                'Sheet Length': `${(sheet.length * 1000).toFixed(0)} mm`,
                'Sheet Width': `${(sheet.width * 1000).toFixed(0)} mm`,
                'Wire Spacing': `${sheet.spacing} mm`,
                'Wire Diameter': `${sheet.wire} mm`,
                'Lap Overlap': `${(overlap * 1000).toFixed(0)} mm`,
            },
            constraints: [],
            geometryData: {
                hostId: this.hostId,
                center: [snapped.x, snapped.y, snapped.z + 0.015],
                sheetLength: sheet.length,
                sheetWidth: sheet.width,
                rotation: 0,
                sheetType: sheet.name,
            },
        };

        this.context.bimStore.addElement(element);
        // Keep the tool active for additional sheets; Esc/back to select ends it.
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point && this.hostId) {
            const sheet = FABRIC_SHEET_TYPES[this.sheetIndex % FABRIC_SHEET_TYPES.length];
            const snapped = this.snapToGrid(point);
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: [
                    new THREE.Vector3(snapped.x - sheet.length / 2, snapped.y - sheet.width / 2, snapped.z),
                    new THREE.Vector3(snapped.x + sheet.length / 2, snapped.y - sheet.width / 2, snapped.z),
                    new THREE.Vector3(snapped.x + sheet.length / 2, snapped.y + sheet.width / 2, snapped.z),
                    new THREE.Vector3(snapped.x - sheet.length / 2, snapped.y + sheet.width / 2, snapped.z),
                ],
                color: '#00bcd4',
            });
        }
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.hostId = null;
            this.context?.appStore.setToolPreview(null);
        } else if (event.key.toLowerCase() === 't') {
            // Cycle fabric type like BIM's type selector
            this.sheetIndex = (this.sheetIndex + 1) % FABRIC_SHEET_TYPES.length;
        } else {
            super.onKeyDown(event);
        }
    }
}

// ==================== Model In-Place (Model panel) ====================

/**
 * Project-specific structural solid: closed footprint sketch + height.
 * Rendered opaque concrete (unlike conceptual Mass).
 */
export interface InPlaceStructuralElement extends Element {
    category: 'Structural In-Place';
    geometryData: {
        footprint: [number, number][];
        height: number;
        elevation: number;
    };
}

export class ModelInPlaceTool extends BaseTool {
    id = 'model_in_place';
    name = 'Model In-Place';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;
    private height = 3.0;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);
        this.points.push(snapped);

        if (this.points.length > 2 && snapped.distanceTo(this.points[0]) < 0.25) {
            this.points.pop();
            this.finishSketch();
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            if (this.points.length > 0) {
                this.context.appStore.setToolPreview({
                    type: 'polygon',
                    points: [...this.points, this.currentPoint],
                    color: '#7ec8e3',
                });
            }
        }
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.points.length >= 3) {
            this.finishSketch();
        } else if (event.key === 'Escape') {
            if (this.points.length > 0) {
                this.points = [];
                this.context?.appStore.setToolPreview(null);
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private finishSketch() {
        if (!this.context || this.points.length < 3) return;

        const element: InPlaceStructuralElement = {
            id: uuidv4(),
            category: 'Structural In-Place',
            type: 'In-Place Structural Member: Concrete',
            name: 'In-Place Structural Member',
            parameters: {
                'Family': 'In-Place Structural Member',
                'Category': this.context.bimStore.toolProperties.inPlaceCategory ?? 'Structural Framing',
                'Height': `${this.height.toFixed(2)} m`,
                'Material': 'Concrete-C25/30',
            },
            constraints: [],
            geometryData: {
                footprint: this.points.map(p => [p.x, p.y] as [number, number]),
                height: this.height,
                elevation: this.points[0].z,
            },
            rebarHostData: {
                validHost: true,
                coverSettings: { top: 25, bottom: 25, sides: 25 },
            },
        };

        this.context.bimStore.addElement(element);
        this.points = [];
        this.currentPoint = null;
        this.context.appStore.setToolPreview(null);
    }
}
